import axios from 'axios';

// API Base URL - ใช้ environment variable หรือ fallback เป็น production URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

// Helper to get full image URL from relative path
export const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;

  const backendHost = API_BASE_URL.replace('/api', '');
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;

  return `${backendHost}${cleanUrl}`;
};

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds timeout (increased for slow queries)
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor - Add auth token (support ทั้ง user token และ admin token)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const isAdminPath = window.location.pathname.toLowerCase().startsWith('/admin');
      if (isAdminPath) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/admin-login';
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Attendees API functions
export const attendeesAPI = {
  // Get all attendees by year and type
  getAttendees: async (year, type = null) => {
    try {
      const params = { year };
      if (type) params.type = type;
      const response = await api.get('/attendees', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching attendees:', error);
      throw error;
    }
  },

  // ดึงทั้ง 3 ประเภทใน request เดียว. timeout 15s แล้วใช้ fallback โหลดทีละประเภท
  getAttendeesByYear: async (year, limit = 50) => {
    try {
      const response = await api.get('/attendees/by-year', {
        params: { year, limit },
        timeout: 15000
      });
      return response.data;
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'));
      if (!isTimeout) console.error('Error fetching attendees by year:', error);
      throw error;
    }
  },

  // Get attendees by type — ใช้เมื่อโหลดทีละประเภท (limit ส่งจาก caller)
  getAttendeesByType: async (year, type, timestamp = null, limit = 50) => {
    try {
      const params = { year, limit };
      if (timestamp) params._t = timestamp;
      const response = await api.get(`/attendees/${type}`, { params, timeout: 20000 });
      return response.data;
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'));
      if (!isTimeout) console.error(`Error fetching ${type} attendees:`, error);
      throw error;
    }
  },

  // Get attendee by ID
  getAttendeeById: async (id) => {
    try {
      const response = await api.get(`/attendees/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching attendee:', error);
      throw error;
    }
  },



  // Export attendees data
  exportAttendees: async (year, type = null) => {
    try {
      const params = { year };
      if (type) params.type = type;
      const response = await api.get('/attendees/export', {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting attendees:', error);
      throw error;
    }
  },

  // Get attendees statistics
  getAttendeesStats: async (year) => {
    try {
      const response = await api.get('/attendees/stats', { params: { year } });
      return response.data.data; // Return the data property from backend response
    } catch (error) {
      console.error('Error fetching attendees stats:', error);
      throw error;
    }
  },

  // Search attendees
  searchAttendees: async (year, searchTerm, filters = {}) => {
    try {
      const params = { year, search: searchTerm, ...filters };
      const response = await api.get('/attendees/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching attendees:', error);
      throw error;
    }
  },

  // Get registrations data (which includes check-in status)
  getRegistrations: async (year, type = null) => {
    try {
      const params = { year };
      if (type) params.type = type;
      const response = await api.get('/registrations', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching registrations:', error);
      throw error;
    }
  },

  // Update check-in status
  updateCheckInStatus: async (registrationId, checkInData) => {
    try {
      const response = await api.put(`/attendees/${registrationId}/checkin`, checkInData);
      return response.data;
    } catch (error) {
      console.error('Error updating check-in status:', error);
      throw error;
    }
  }
};

// Exhibitions API functions
export const exhibitionsAPI = {
  // Get all exhibitions
  getExhibitions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/exhibitions?${params}`);

      // Format image and PDF URLs
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        response.data.data = response.data.data.map(item => ({
          ...item,
          image_url: getImageUrl(item.image_url),
          pdf_url: getImageUrl(item.pdf_url)
        }));
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching exhibitions:', error);
      throw error;
    }
  },

  // Get single exhibition by ID
  getExhibition: async (id) => {
    try {
      const response = await api.get(`/exhibitions/${id}`);

      // Format image and PDF URLs
      if (response.data && response.data.success && response.data.data) {
        response.data.data.image_url = getImageUrl(response.data.data.image_url);
        response.data.data.pdf_url = getImageUrl(response.data.data.pdf_url);
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching exhibition:', error);
      throw error;
    }
  },

  // Create new exhibition
  createExhibition: async (exhibitionData) => {
    try {
      const formData = new FormData();

      // Add exhibition name
      if (exhibitionData.name) {
        formData.append('name', exhibitionData.name.trim());
      }

      // Add exhibition position
      if (exhibitionData.position) {
        formData.append('position', exhibitionData.position.trim());
      }



      // Add image file if exists
      if (exhibitionData.imageFile && exhibitionData.imageFile instanceof File) {
        formData.append('image', exhibitionData.imageFile);
      }

      // Add PDF file if exists
      if (exhibitionData.pdfFile && exhibitionData.pdfFile instanceof File) {
        formData.append('pdf', exhibitionData.pdfFile);
      }

      const response = await api.post('/exhibitions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating exhibition:', error);
      throw error;
    }
  },

  // Update exhibition
  updateExhibition: async (id, exhibitionData) => {
    try {
      const formData = new FormData();

      // Add exhibition name if provided
      if (exhibitionData.name) {
        formData.append('name', exhibitionData.name.trim());
      }

      // Add exhibition position if provided
      if (exhibitionData.position !== undefined) {
        formData.append('position', exhibitionData.position.trim());
      }



      // Add new image file if provided
      if (exhibitionData.imageFile && exhibitionData.imageFile instanceof File) {
        formData.append('image', exhibitionData.imageFile);
      }

      // Add new PDF file if provided
      if (exhibitionData.pdfFile && exhibitionData.pdfFile instanceof File) {
        formData.append('pdf', exhibitionData.pdfFile);
      }

      const response = await api.put(`/exhibitions/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating exhibition:', error);
      throw error;
    }
  },

  // Delete exhibition
  deleteExhibition: async (id) => {
    try {
      const response = await api.delete(`/exhibitions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting exhibition:', error);
      throw error;
    }
  },

  // Get exhibition categories
  getCategories: async () => {
    try {
      const response = await api.get('/exhibitions/categories/list');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Upload file
  uploadFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Format URL if it's relative
      if (response.data && response.data.fileUrl) {
        response.data.fileUrl = getImageUrl(response.data.fileUrl);
      }

      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  // Upload multiple files
  uploadMultipleFiles: async (files) => {
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      const response = await api.post('/upload/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading multiple files:', error);
      throw error;
    }
  },

  // Delete file from S3
  deleteFile: async (fileKey) => {
    try {
      const response = await api.delete(`/upload/delete?key=${encodeURIComponent(fileKey)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }
};

// Speakers API functions
export const speakersAPI = {
  // Get all speakers
  getSpeakers: async (params = {}) => {
    try {
      const response = await api.get('/speakers', { params });

      // Format photo and PDF URLs
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        response.data.data = response.data.data.map(item => ({
          ...item,
          photo_url: getImageUrl(item.photo_url),
          pdf_url: getImageUrl(item.pdf_url)
        }));
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching speakers:', error);
      throw error;
    }
  },

  // Get speaker by ID
  getSpeaker: async (id) => {
    try {
      const response = await api.get(`/speakers/${id}`);

      // Format photo and PDF URLs
      if (response.data && response.data.success && response.data.data) {
        response.data.data.photo_url = getImageUrl(response.data.data.photo_url);
        response.data.data.pdf_url = getImageUrl(response.data.data.pdf_url);
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching speaker:', error);
      throw error;
    }
  },

  // Create new speaker with file uploads
  createSpeaker: async (speakerData) => {
    try {
      const formData = new FormData();

      // Add speaker name
      if (speakerData.name) {
        formData.append('name', speakerData.name.trim());
      }

      // Add speaker position
      if (speakerData.position) {
        formData.append('position', speakerData.position.trim());
      }

      // Add photo file if exists
      if (speakerData.photoFile && speakerData.photoFile instanceof File) {
        formData.append('photo', speakerData.photoFile);
      }

      // Add PDF file if exists
      if (speakerData.pdfFile && speakerData.pdfFile instanceof File) {
        formData.append('pdf', speakerData.pdfFile);
      }

      const response = await api.post('/speakers', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating speaker:', error);
      throw error;
    }
  },

  // Update speaker
  updateSpeaker: async (id, speakerData) => {
    try {
      const formData = new FormData();

      // Add speaker name if provided
      if (speakerData.name) {
        formData.append('name', speakerData.name.trim());
      }

      // Add speaker position if provided
      if (speakerData.position !== undefined) {
        formData.append('position', speakerData.position.trim());
      }

      // Add new photo file if provided
      if (speakerData.photoFile && speakerData.photoFile instanceof File) {
        formData.append('photo', speakerData.photoFile);
      }

      // Add new PDF file if provided
      if (speakerData.pdfFile && speakerData.pdfFile instanceof File) {
        formData.append('pdf', speakerData.pdfFile);
      }

      const response = await api.put(`/speakers/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating speaker:', error);
      throw error;
    }
  },

  // Delete speaker (soft delete)
  deleteSpeaker: async (id) => {
    try {
      const response = await api.delete(`/speakers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting speaker:', error);
      throw error;
    }
  },

  // Permanently delete speaker
  permanentDeleteSpeaker: async (id) => {
    try {
      const response = await api.delete(`/speakers/${id}/permanent`);
      return response.data;
    } catch (error) {
      console.error('Error permanently deleting speaker:', error);
      throw error;
    }
  },

  // File validation helpers
  validateImageFile: (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      throw new Error('รูปภาพต้องเป็นไฟล์ JPEG, PNG, GIF หรือ WebP เท่านั้น');
    }

    if (file.size > maxSize) {
      throw new Error('รูปภาพมีขนาดใหญ่เกินไป (สูงสุด 10MB)');
    }

    return true;
  },

  validatePdfFile: (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.type !== 'application/pdf') {
      throw new Error('ต้องเป็นไฟล์ PDF เท่านั้น');
    }

    if (file.size > maxSize) {
      throw new Error('ไฟล์ PDF มีขนาดใหญ่เกินไป (สูงสุด 10MB)');
    }

    return true;
  }
};

export default api; 